import { Config } from '../config';

const getToken = () => localStorage.getItem('access_token');

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

/**
 * DigitalOcean Functions answers its own failures — a cold start that overruns,
 * an action that dies — with a *success* status and a JSON body of its own:
 *
 *   HTTP 202  content-type: application/json
 *   {"code":"6858116d3645e3e2b2e415dd0f629f22","error":"Response not returned…"}
 *
 * Observed live on /health at 00:58 on 2026-08-12, right after a deploy. This is
 * the shape behind the original marketplace crash: `res.ok` is true, the body
 * parses, and `{code, error}` lands in `setCategories()` where an array was
 * expected — `f.map is not a function`, with nothing in the trace pointing at the
 * backend.
 *
 * Rejecting non-JSON was not enough, because this *is* JSON. The envelope is
 * recognisable and cannot be confused with ours: FastAPI reports errors as
 * `{detail}`, and no endpoint here returns a `code`/`error` pair.
 */
function isPlatformError(body: unknown): body is { code: string; error: string } {
  if (typeof body !== 'object' || body === null) return false;
  const b = body as Record<string, unknown>;
  return typeof b.error === 'string' && typeof b.code === 'string';
}

/**
 * "Response not yet ready" is the cold-start face of the envelope above: the
 * container took longer to boot than DO's ~40s synchronous window, so the
 * platform answers on the action's behalf while the action keeps running.
 *
 * Measured 2026-09-02 on /v1/auth/register: 40s → this envelope on a cold
 * container, 0.29s on a warm one. So the *next* attempt almost always works,
 * which is what makes the distinction worth drawing for the user — "unavailable"
 * reads as "come back tomorrow" when the truth is "press it again".
 */
function isColdStart(body: { error: string }): boolean {
  return /not yet ready|not returned/i.test(body.error);
}

// Retrying is only safe where a repeat cannot double an effect. A registration
// POST that hit this envelope may still be running — it can create the account
// after the platform has already given up on it — so retrying it automatically
// would race the first attempt and surface "Email already registered" for an
// account the user is in the middle of creating. Ask, don't retry.
const RETRY_SAFE = new Set(['GET', 'HEAD']);

async function request<T>(endpoint: string, init: RequestInit = {}, attempt = 0): Promise<T> {
  const url = `${Config.API_BASE_URL}${endpoint}`;
  const token = getToken();
  const headers = new Headers({ 'Content-Type': 'application/json' });
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init.headers) new Headers(init.headers as HeadersInit).forEach((v, k) => headers.set(k, v));

  const res = await fetch(url, { ...init, headers });

  if (res.status === 401) {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.location.href = '/auth/login';
    throw new ApiError(401, 'Unauthorized');
  }

  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      detail = body?.detail ?? detail;
    } catch { /* ignore */ }
    throw new ApiError(res.status, detail);
  }

  // 204/205 and empty bodies are legitimate (DELETE returns no content).
  if (res.status === 204 || res.status === 205) return undefined as T;

  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    const body = await res.json();
    if (isPlatformError(body)) {
      const method = (init.method ?? 'GET').toUpperCase();
      if (isColdStart(body)) {
        if (attempt === 0 && RETRY_SAFE.has(method)) {
          // The first request paid for the boot; the container is warm now.
          return request<T>(endpoint, init, attempt + 1);
        }
        throw new ApiError(
          res.status,
          'The server was still waking up and did not answer in time. Please try again — it should be quick now.',
        );
      }
      throw new ApiError(res.status, `Backend unavailable (${body.error})`);
    }
    return body as T;
  }

  // Anything else is a lie about the shape. Returning res.text() here used to
  // hand callers a string typed as T, so an SPA-fallback HTML page or a
  // serverless error envelope became `setCategories("<!doctype html>…")` and the
  // page died on `.map is not a function` with no clue where it came from.
  // Reject instead: every caller already handles a failed request.
  const body = (await res.text()).trim();
  if (!body) return undefined as T;
  throw new ApiError(res.status, `Expected JSON from ${endpoint}, received ${ct || 'no content-type'}`);
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body !== undefined ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body !== undefined ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** Send FormData (multipart/form-data). Omits Content-Type so the browser sets the boundary. */
  postForm: async <T>(path: string, form: FormData): Promise<T> => {
    const url = `${Config.API_BASE_URL}${path}`;
    const token = getToken();
    const headers = new Headers();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    const res = await fetch(url, { method: 'POST', headers, body: form });
    if (res.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/auth/login';
      throw new ApiError(401, 'Unauthorized');
    }
    if (!res.ok) {
      let detail = `HTTP ${res.status}`;
      try { const b = await res.json(); detail = b?.detail ?? detail; } catch { /* ignore */ }
      throw new ApiError(res.status, detail);
    }
    return res.json() as Promise<T>;
  },
};

/** @deprecated use api.* helpers; kept for legacy vanilla-TS callers */
export async function apiCall<T = unknown>(endpoint: string, init: RequestInit = {}) {
  try {
    const data = await request<T>(endpoint, init);
    return { ok: true, status: 200, data };
  } catch (e: unknown) {
    if (e instanceof ApiError) return { ok: false, status: e.status, error: e.message };
    return { ok: false, status: 0, error: (e as Error)?.message ?? 'Network error' };
  }
}
